import React from 'react';
import { useTranslation } from 'react-i18next';

import { ManageContractsApp } from './contractManagementContext';

import styles from './styles/ManageContractsAddressFields.module.scss';

// Types.
import { ManageContractsAddressFieldProps } from '../../../types/contract';
import { InputAddress } from '../../../types/address';
import { validateAddressString, validateStringWithoutNumbers, validateZipCode } from '../../../utils/contact';

export const ManageContractsAddressFields: React.FunctionComponent<ManageContractsAddressFieldProps> = (props: ManageContractsAddressFieldProps) => {
  const { t } = useTranslation();
  const dispatch = ManageContractsApp.useDispatch();
  const { showNameFields, showSSN, showMobilePhoneNumber, address, disabled, action } = props;

  const { payment } = ManageContractsApp.useState();

  let onChangeFunc = (e: React.ChangeEvent<HTMLInputElement>, dispatchAction: any, field: string) => {}; // eslint-disable-line

  if (action !== 'changePayment') {
    onChangeFunc = (e: React.ChangeEvent<HTMLInputElement>, dispatchAction: 'newAddress' | 'multipleOwnersNewAddress', field: string) => {
      dispatch([dispatchAction, { ...address, [field]: e.target.value }]);
    };
  }

  if (action === 'changePayment') {
    onChangeFunc = (e: React.ChangeEvent<HTMLInputElement>, dispatchAction: 'changePayment', field: string) => {
      dispatch([dispatchAction, { ...payment, paymentTypeNewAddress: { ...address, [field]: e.target.value } }]);
    };
  }

  const [addressIsValid, setAddressIsValid] = React.useState<boolean | undefined>();
  const [houseNumberIsValid, setHouseNumberIsValid] = React.useState<boolean | undefined>();
  const [houseLetterIsValid, setHouseLetterIsValid] = React.useState<boolean | undefined>();
  const [residenceIsValid, setResidenceIsValid] = React.useState<boolean | undefined>();
  const [zipCodeIsValid, setZipCodeIsValid] = React.useState<boolean | undefined>();

  const validateAddressFields = (event: React.ChangeEvent<HTMLInputElement>, dispatchAction: any, field: string): void => {
    const { name, value } = event.target;
    const fieldName: keyof InputAddress = name as keyof InputAddress;
    const newAddress: InputAddress = { ...address };

    newAddress[fieldName] = value;
    onChangeFunc(event, dispatchAction, field);

    const streetName = fieldName === 'streetName' ? value : (address?.streetName ?? '');
    const houseNumber = fieldName === 'houseNumber' ? value : (address?.houseNumber ?? '');
    const houseLetter = fieldName === 'houseLetter' ? value : (address?.houseLetter ?? '');
    const residence = fieldName === 'residence' ? value : (address?.residence ?? '');
    const zipCode = fieldName === 'zipCode' ? value : (address?.zipCode ?? '');

    const addressCheck = validateStringWithoutNumbers(streetName);
    setAddressIsValid(addressCheck);

    const houseNumberCheck = houseNumber.length === 0 || validateAddressString(houseNumber) ? true : false;
    setHouseNumberIsValid(houseNumberCheck);
    const houseLetterCheck =
      residence.length > 0
        ? houseLetter.length > 0 && validateAddressString(houseLetter)
          ? true
          : false
        : houseLetter.length > 0
          ? validateAddressString(houseLetter)
          : true;
    setHouseLetterIsValid(houseLetterCheck);
    const residenceCheck = residence.length === 0 || validateAddressString(residence) ? true : false;
    setResidenceIsValid(residenceCheck);

    const zipCodeCheck = validateZipCode(zipCode) ? true : false;
    setZipCodeIsValid(zipCodeCheck);
  };

  return (
    <div className="addressFields">
      {showNameFields && (
        <div className="firstname">
          <label htmlFor="firstname">{t('common:general.firstName')}*</label>
          <input
            type="text"
            id="firstname"
            name="firstName"
            value={address?.firstname}
            onChange={(e): void => validateAddressFields(e, action, 'firstname')}
            required
            disabled={disabled ? true : false}
          />
        </div>
      )}
      {showNameFields && (
        <div className="lastname">
          <label htmlFor="lastname">{t('common:general.lastName')}*</label>
          <input
            type="text"
            id="lastname"
            name="lastName"
            value={address?.lastname}
            onChange={(e): void => validateAddressFields(e, action, 'lastname')}
            required
            disabled={disabled ? true : false}
          />
        </div>
      )}
      {showSSN && (
        <div className="ssn">
          <label htmlFor="ssn">{t('common:general.SSN')}*</label>
          <input
            type="text"
            id="ssn"
            name="ssn"
            value={address?.ssn}
            onChange={(e): void => validateAddressFields(e, action, 'ssn')}
            required
            disabled={disabled ? true : false}
          />
        </div>
      )}
      {showNameFields && !showSSN && <div className="break" />}
      <div className="address">
        <label htmlFor="address">{t('common:general.streetAddress')}*</label>
        <input
          type="text"
          id="address"
          name="streetName"
          value={address?.streetName}
          onChange={(e): void => validateAddressFields(e, action, 'streetName')}
          required
          pattern="^([^0-9]*)$"
          disabled={disabled ? true : false}
          onInput={(event) => {
            const target = event.target as HTMLInputElement;
            const addressIsValid = new RegExp('^([^0-9]*)$').test(target.value);

            if (!addressIsValid) {
              return target.setCustomValidity(t(`manageContracts:validation.addressNoNumbers`));
            }
            return target.setCustomValidity('');
          }}
        />
        {addressIsValid === false && (
          <span className={styles.validation} aria-live="polite">
            {t(`manageContracts:validation.addressNoNumbers`)}
          </span>
        )}
      </div>
      <div className="houseNumber">
        <label htmlFor="house">{t('common:general.houseNumber')}</label>
        <input
          type="text"
          id="house"
          name="houseNumber"
          value={address?.houseNumber}
          onChange={(e): void => validateAddressFields(e, action, 'houseNumber')}
          disabled={disabled ? true : false}
          className={houseNumberIsValid === false ? 'invalid' : ''}
        />
        {houseNumberIsValid === false && (
          <span className={styles.validation} aria-live="polite">
            {t(`manageContracts:validation.checkInput`)}
          </span>
        )}
      </div>
      <div className="houseLetter">
        <label htmlFor="houseLetter">{t('common:general.houseLetter')}</label>
        <input
          type="text"
          id="houseLetter"
          name="houseLetter"
          value={address?.houseLetter}
          onChange={(e): void => validateAddressFields(e, action, 'houseLetter')}
          required={address?.residence && address?.residence.length > 0 ? true : false}
          disabled={disabled ? true : false}
          className={houseLetterIsValid === false ? 'invalid' : ''}
        />
        {houseLetterIsValid === false && (
          <span className={styles.validation} aria-live="polite">
            {t(`manageContracts:validation.checkInput`)}
          </span>
        )}
      </div>
      <div className="residence">
        <label htmlFor="residence">{t('common:general.residence')}</label>
        <input
          type="text"
          id="residence"
          name="residence"
          value={address?.residence}
          onChange={(e): void => validateAddressFields(e, action, 'residence')}
          disabled={disabled ? true : false}
          className={residenceIsValid === false ? 'invalid' : ''}
        />
        {residenceIsValid === false && (
          <span className={styles.validation} aria-live="polite">
            {t(`manageContracts:validation.checkInput`)}
          </span>
        )}
      </div>
      <div className="zipcode">
        <label htmlFor="zipcode">{t('common:general.zipcode')}*</label>
        <input
          type="text"
          id="zipcode"
          name="zipCode"
          value={address?.zipCode}
          onChange={(e): void => validateAddressFields(e, action, 'zipCode')}
          required
          disabled={disabled ? true : false}
          className={zipCodeIsValid === false ? 'invalid' : ''}
          pattern="^\d{5}$"
        />
        {zipCodeIsValid === false && (
          <span className={styles.validation} aria-live="polite">
            {t(`manageContracts:validation.checkInput`)}
          </span>
        )}
      </div>
      <div className="postalArea">
        <label htmlFor="city">{t('common:general.postalArea')}*</label>
        <input
          type="text"
          id="city"
          name="city"
          value={address?.cityName}
          onChange={(e): void => validateAddressFields(e, action, 'cityName')}
          required
          disabled={disabled ? true : false}
        />
      </div>
      {showMobilePhoneNumber && (
        <div className="mobileNumber">
          <label htmlFor="mobileNumber">{t('common:general.phone')}*</label>
          <input
            type="text"
            id="mobileNumber"
            name="mobileNumber"
            value={address?.mobileNumber}
            onChange={(e): void => validateAddressFields(e, action, 'mobileNumber')}
            required
            disabled={disabled ? true : false}
          />
        </div>
      )}
    </div>
  );
};

export default ManageContractsAddressFields;
